import { useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Spinner from "../../components/Spinner";
import useProfileData from "../hooks/useProfileData";

export default function ProfilePageView() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const {
    user,
    loading,
    displayName,
    setDisplayName,
    saving,
    saved,
    error,
    avatarPreview,
    uploading,
    uploadError,
    deleteConfirm,
    setDeleteConfirm,
    deleting,
    deleteError,
    setDeleteError,
    saveProfile,
    uploadAvatar,
    logout,
    deleteAccount,
  } = useProfileData(navigate);

  const handleAvatarClick = () => fileInputRef.current?.click();

  if (loading) return (
    <div style={{ background: "var(--bg-primary)", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, paddingTop: 52 }}>
      <Spinner />
      <span style={{ fontSize: 13, color: "var(--text-muted)", letterSpacing: "-.01em" }}>Loading…</span>
    </div>
  );

  const currentPhoto = avatarPreview || user?.photoURL;
  const initials = (user?.displayName || user?.email || "?").slice(0, 2).toUpperCase();
  const memberSince = user?.metadata?.creationTime
    ? new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(new Date(user.metadata.creationTime))
    : null;

  return (
    <div style={{ background: "var(--bg-primary)", minHeight: "100vh", paddingTop: 52 }}>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "40px 24px 80px" }}>

        {/* Back button */}
        <Link
          to="/dashboard"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 13, color: "var(--text-muted)", textDecoration: "none",
            marginBottom: 32, transition: "color .15s",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
          </svg>
          Back to Dashboard
        </Link>

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.02em", margin: 0 }}>Profile</h1>
        </div>

        {/* Avatar card */}
        <div style={{
          background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
          borderRadius: 16, padding: "24px", marginBottom: 12,
          display: "flex", alignItems: "center", gap: 20,
        }}>
          {/* Clickable avatar */}
          <div onClick={handleAvatarClick} className="avatar-wrap" style={{ flexShrink: 0 }}>
            {currentPhoto ? (
              <img src={currentPhoto} alt="Avatar"
                style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", display: "block" }} />
            ) : (
              <div style={{
                width: 64, height: 64, borderRadius: "50%",
                background: "linear-gradient(135deg,#818cf8,#a78bfa)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 22, fontWeight: 700, color: "#fff",
              }}>{initials}</div>
            )}
            {/* Camera hover overlay */}
            <div className="avatar-overlay" style={{
              position: "absolute", inset: 0, borderRadius: "50%",
              background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center",
              opacity: uploading ? 1 : 0, transition: "opacity .15s",
            }}>
              {uploading
                ? <span style={{ fontSize: 11, color: "#fff" }}>…</span>
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>
                  </svg>
              }
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={uploadAvatar} style={{ display: "none" }} />

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.01em", marginBottom: 3 }}>
              {user.displayName || "No name set"}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
            {memberSince && <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4 }}>Member since {memberSince}</div>}
          </div>
          <button
            onClick={handleAvatarClick}
            disabled={uploading}
            style={{
              background: "var(--bg-ghost)", border: "1px solid var(--border-subtle)",
              borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 500,
              color: "var(--text-secondary)", cursor: uploading ? "not-allowed" : "pointer",
              fontFamily: "inherit", flexShrink: 0, transition: "background .15s",
            }}
          >
            {uploading ? "Uploading…" : "Change photo"}
          </button>
        </div>

        {uploadError && (
          <div style={{ fontSize: 12, color: "#f87171", marginBottom: 10, padding: "8px 14px", background: "rgba(248,113,113,.08)", borderRadius: 8 }}>
            {uploadError}
          </div>
        )}

        {/* Edit form */}
        <div style={{
          background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
          borderRadius: 16, padding: "24px", marginBottom: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 20 }}>Personal information</div>
          <form onSubmit={saveProfile}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6, letterSpacing: ".05em", textTransform: "uppercase" }}>
                Display name
              </label>
              <input
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="Your name"
                style={{
                  width: "100%", background: "var(--bg-ghost)", border: "1px solid var(--border-color)",
                  borderRadius: 8, padding: "10px 14px", fontSize: 14, color: "var(--text-primary)",
                  outline: "none", boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6, letterSpacing: ".05em", textTransform: "uppercase" }}>
                Email address
              </label>
              <input
                value={user.email}
                disabled
                style={{
                  width: "100%", background: "var(--bg-ghost)", border: "1px solid var(--border-color)",
                  borderRadius: 8, padding: "10px 14px", fontSize: 14, color: "var(--text-dim)",
                  outline: "none", boxSizing: "border-box", cursor: "not-allowed", opacity: 0.5,
                }}
              />
              <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 5 }}>Email changes are not supported yet.</div>
            </div>

            {error && <div style={{ fontSize: 13, color: "#f87171", marginBottom: 14, padding: "10px 14px", background: "rgba(248,113,113,.08)", borderRadius: 8 }}>{error}</div>}

            <button type="submit" disabled={saving} className="btn btn-white" style={{ fontSize: 13, padding: "9px 22px" }}>
              {saving ? "Saving…" : saved ? "Saved ✓" : "Save changes"}
            </button>
          </form>
        </div>

        {/* Email verification */}
        <div style={{
          background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
          borderRadius: 16, padding: "18px 24px", marginBottom: 12,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>Email verification</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {user.emailVerified ? "Your email address is verified." : "Your email is not yet verified."}
            </div>
          </div>
          <div style={{
            fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 999, flexShrink: 0,
            background: user.emailVerified ? "rgba(52,211,153,.12)" : "rgba(251,191,36,.10)",
            color: user.emailVerified ? "#34d399" : "#fbbf24",
          }}>
            {user.emailVerified ? "Verified" : "Unverified"}
          </div>
        </div>

        {/* Sign out */}
        <div style={{
          background: "var(--bg-card)", border: "1px solid rgba(248,113,113,.18)",
          borderRadius: 16, padding: "18px 24px", marginBottom: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 3 }}>Sign out</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>You will be redirected to the login page.</div>
          <button onClick={logout} className="btn btn-outline" style={{ fontSize: 13, padding: "8px 20px", color: "#f87171", borderColor: "rgba(248,113,113,.3)" }}>
            Sign out
          </button>
        </div>

        {/* Delete account */}
        <div style={{
          background: "var(--bg-card)", border: "1px solid rgba(248,113,113,.28)",
          borderRadius: 16, padding: "18px 24px",
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#f87171", marginBottom: 3 }}>Delete account</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16, lineHeight: 1.6 }}>
            This is permanent. All your data, API keys, and usage history will be deleted immediately and cannot be recovered.
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6, letterSpacing: ".05em", textTransform: "uppercase" }}>
              Type <span style={{ color: "var(--text-primary)", fontStyle: "normal" }}>delete my account</span> to confirm
            </label>
            <input
              value={deleteConfirm}
              onChange={e => { setDeleteConfirm(e.target.value); setDeleteError(""); }}
              placeholder="delete my account"
              autoComplete="off"
              style={{
                width: "100%", background: "var(--bg-ghost)", border: "1px solid var(--border-color)",
                borderRadius: 8, padding: "10px 14px", fontSize: 14, color: "var(--text-primary)",
                outline: "none", boxSizing: "border-box",
              }}
            />
          </div>
          {deleteError && (
            <div style={{ fontSize: 12, color: "#f87171", marginBottom: 12, padding: "8px 12px", background: "rgba(248,113,113,.08)", borderRadius: 8 }}>
              {deleteError}
            </div>
          )}
          <button
            onClick={deleteAccount}
            disabled={deleteConfirm !== "delete my account" || deleting}
            style={{
              background: deleteConfirm === "delete my account" ? "rgba(248,113,113,.12)" : "var(--bg-ghost)",
              border: "1px solid rgba(248,113,113,.3)",
              borderRadius: 8, padding: "8px 20px", fontSize: 13, fontWeight: 500,
              color: deleteConfirm === "delete my account" ? "#f87171" : "var(--text-dim)",
              cursor: deleteConfirm === "delete my account" && !deleting ? "pointer" : "not-allowed",
              fontFamily: "inherit", transition: "all .15s",
              opacity: deleting ? 0.6 : 1,
            }}
          >
            {deleting ? "Deleting…" : "Delete my account"}
          </button>
        </div>

      </div>
    </div>
  );
}
